// In-memory stand-in for the dormant Concierge backend. Every shape here is
// copied from the live code (routes/api/support.php, SupportConversationController,
// ChatSyncController, VisitorTokenController, WidgetConfigService), so flipping
// the mock off at the end of the build is a no-op rather than a rewrite.
// Timestamps use PHP's toIso8601String() format, not JS toISOString().

const LIST_MAX_LIMIT = 50;
const SYNC_DEFAULT_LIMIT = 100;
const SYNC_MAX_LIMIT = 200;
const LATENCY_MS = 140;
const AGENT_REPLY_MS = 6000;

function iso(date) {
  return (date || new Date()).toISOString().slice(0, 19) + "+00:00";
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function ok(data) {
  await wait(LATENCY_MS);
  return { status: 200, data };
}

async function fail(status, data) {
  await wait(LATENCY_MS);
  return { status, data };
}

let messageSeq = 1000;
let conversationSeq = 100;
let referenceSeq = 4200;

const state = {
  conversations: [],
};

function newConversation(attrs) {
  conversationSeq += 1;
  referenceSeq += 1;
  const at = iso();
  const record = {
    id: conversationSeq,
    uuid: "mock-uuid-" + conversationSeq,
    reference: "GCC-" + referenceSeq,
    subject: attrs.subject || null,
    status: "open",
    channel_type: attrs.channel_type || "widget",
    assignee_name: null,
    created_at: at,
    updated_at: at,
    last_read_message_id: 0,
    csat_submitted: false,
    messages: [],
  };
  state.conversations.push(record);
  return record;
}

function addMessage(conversation, attrs) {
  messageSeq += 1;
  const message = {
    id: messageSeq,
    body: attrs.body,
    author_type: attrs.author_type,
    author_name: attrs.author_name || null,
    visibility: "public",
    delivery_status: "delivered",
    client_ref: attrs.client_ref || null,
    created_at: iso(),
  };
  conversation.messages.push(message);
  conversation.updated_at = message.created_at;
  return message;
}

// Agent-side authorship drives the unread badge, so a scripted reply lets the
// poll and unread pipelines be exercised without a live agent console.
function scheduleAgentReply(conversation) {
  setTimeout(() => {
    if (conversation.status === "closed") {
      return;
    }
    addMessage(conversation, {
      body: "Thanks for reaching out. An agent is looking at this now and will reply shortly.",
      author_type: "admin",
      author_name: "Marta",
    });
    conversation.assignee_name = "Marta";
  }, AGENT_REPLY_MS);
}

function unreadCount(conversation) {
  return conversation.messages.filter(
    (m) =>
      ["admin", "bot", "system"].includes(m.author_type) &&
      m.visibility === "public" &&
      m.delivery_status !== "suppressed" &&
      m.id > conversation.last_read_message_id
  ).length;
}

function lastMessageExcerpt(conversation) {
  const visible = conversation.messages.filter((m) => m.visibility === "public");
  if (!visible.length) {
    return null;
  }
  const body = (visible[visible.length - 1].body || "").trim();
  return body.length > 140 ? body.slice(0, 140) + "..." : body;
}

function conversationBlock(conversation) {
  return {
    reference: conversation.reference,
    uuid: conversation.uuid,
    subject: conversation.subject,
    status: conversation.status,
    channel_type: conversation.channel_type,
    created_at: conversation.created_at,
    unread_count: unreadCount(conversation),
  };
}

function publicMessages(conversation) {
  return conversation.messages
    .filter((m) => m.visibility === "public" && m.delivery_status !== "suppressed")
    .map((m) => ({
      id: m.id,
      body: m.body,
      author_type: m.author_type,
      author_name: m.author_name,
      visibility: m.visibility,
      created_at: m.created_at,
      delivery_status: m.delivery_status,
    }));
}

function csatEligible(conversation) {
  return ["solved", "closed"].includes(conversation.status) && !conversation.csat_submitted;
}

function findByReference(reference) {
  return state.conversations.find((c) => c.reference === reference) || null;
}

function notFound() {
  return fail(404, { message: "Not found" });
}

function seed() {
  if (state.conversations.length) {
    return;
  }
  const solved = newConversation({ subject: "Withdrawal still pending", channel_type: "widget" });
  addMessage(solved, {
    body: "My withdrawal from Tuesday is still showing as pending.",
    author_type: "player",
  });
  addMessage(solved, {
    body: "That payout cleared this morning, it should be with your bank within one working day.",
    author_type: "admin",
    author_name: "Marta",
  });
  solved.status = "solved";
  solved.assignee_name = "Marta";
  solved.last_read_message_id = messageSeq;

  const open = newConversation({ subject: "Bonus wagering question", channel_type: "form" });
  addMessage(open, {
    body: "How much wagering is left on my welcome bonus?",
    author_type: "player",
  });
  addMessage(open, {
    body: "You have 120 EUR of wagering left. It expires on 30 July.",
    author_type: "admin",
    author_name: "Ruben",
  });
  open.assignee_name = "Ruben";
}

const supportMocks = {
  // POST /visitor-token
  async mintVisitorToken() {
    return ok({
      visitor_token: "mock-visitor-" + Math.random().toString(36).slice(2, 12),
      expires_in: 60 * 60 * 24 * 30,
    });
  },

  // GET /widget-config
  // forms arrives as [] (not {}) whenever no ticket_form is published, because
  // the backend casts an empty object back to a PHP array before serialising.
  async widgetConfig(locale) {
    seed();
    return ok({
      brand: "play365",
      direction: locale === "ar" || locale === "he" ? "rtl" : "ltr",
      theme: {
        css_vars: { light: {}, dark: {} },
        position: "bottom-right",
        logo_asset_id: null,
        avatar_asset_id: null,
      },
      launcher: {},
      departments: [
        { key: "general", slug: "general", name: "General", i18n_key: "support.department.general" },
        { key: "payments", slug: "payments", name: "Payments", i18n_key: "support.department.payments" },
        { key: "accounts", slug: "accounts", name: "Account", i18n_key: "support.department.accounts" },
      ],
      forms: {
        general: [
          { key: "subject", type: "text", label: "Subject", required: true, max: 255 },
          { key: "message", type: "textarea", label: "How can we help?", required: true },
        ],
        payments: [
          { key: "subject", type: "text", label: "Subject", required: true, max: 255 },
          {
            key: "transaction_type",
            type: "select",
            label: "Transaction type",
            required: true,
            options: [
              { value: "deposit", label: "Deposit" },
              { value: "withdrawal", label: "Withdrawal" },
            ],
          },
          { key: "message", type: "textarea", label: "Describe the issue", required: true },
        ],
      },
      business_hours: {
        spec: {
          timezone: "Europe/Malta",
          spans: [
            { day: 1, from: "08:00", to: "22:00" },
            { day: 2, from: "08:00", to: "22:00" },
            { day: 3, from: "08:00", to: "22:00" },
            { day: 4, from: "08:00", to: "22:00" },
            { day: 5, from: "08:00", to: "22:00" },
          ],
          holidays: [],
        },
        online: true,
        next_open_at: null,
      },
      announcements: [],
      quick_replies: [],
      features: {
        chat: true,
        email_inbound: false,
        proactive: false,
        attachments: false,
        csat: true,
        kb: true,
      },
      strings: {
        ui: {
          greeting: "Hi there. How can we help you today?",
          offline: "We are offline right now. Leave a message and we will reply by email.",
        },
        consent: {},
        rg: {},
      },
      transport: {
        ws_enabled: false,
        ws_host: null,
        ws_key: null,
        poll_interval_ms: 12000,
      },
      config_versions: { "widget_theme:default": 1 },
      version: "mock-config-v1",
    });
  },

  // POST /conversations
  async createConversation(payload) {
    seed();
    const body = payload || {};
    if (!body.message) {
      return fail(422, { validationErrors: { message: "The message field is required." } });
    }
    const conversation = newConversation({
      subject: body.subject,
      channel_type: body.form_id ? "form" : "widget",
    });
    const message = addMessage(conversation, { body: body.message, author_type: "player" });
    scheduleAgentReply(conversation);
    return ok({
      conversation: conversationBlock(conversation),
      message: {
        id: message.id,
        body: message.body,
        author_type: message.author_type,
        created_at: message.created_at,
      },
    });
  },

  // GET /conversations
  async listConversations(params) {
    seed();
    const query = params || {};
    let limit = parseInt(query.limit, 10);
    limit = Number.isNaN(limit) ? LIST_MAX_LIMIT : Math.max(1, Math.min(limit, LIST_MAX_LIMIT));

    const sorted = state.conversations
      .slice()
      .sort((a, b) => (a.updated_at === b.updated_at ? b.id - a.id : a.updated_at < b.updated_at ? 1 : -1));

    const offset = query.cursor ? parseInt(query.cursor, 10) || 0 : 0;
    const page = sorted.slice(offset, offset + limit);
    const hasMore = sorted.length > offset + limit;

    return ok({
      data: page.map((c) => ({
        reference: c.reference,
        subject: c.subject,
        status: c.status,
        last_message_excerpt: lastMessageExcerpt(c),
        last_activity_at: c.updated_at,
        unread_count: unreadCount(c),
      })),
      next_cursor: hasMore ? String(offset + limit) : null,
    });
  },

  // GET /conversations/{reference}
  async getConversation(reference) {
    seed();
    const conversation = findByReference(reference);
    if (!conversation) {
      return notFound();
    }
    return ok({
      conversation: conversationBlock(conversation),
      messages: publicMessages(conversation),
      csat: { eligible: csatEligible(conversation) },
    });
  },

  // POST /conversations/{reference}/messages
  async postMessage(reference, payload) {
    const conversation = findByReference(reference);
    if (!conversation) {
      return notFound();
    }
    const body = payload || {};
    if (!body.body) {
      return fail(422, { validationErrors: { body: "The body field is required." } });
    }
    const message = addMessage(conversation, {
      body: body.body,
      author_type: "player",
      client_ref: body.client_ref,
    });
    scheduleAgentReply(conversation);
    return ok({
      message: {
        id: message.id,
        client_ref: body.client_ref || null,
        created_at: message.created_at,
        delivery_status: message.delivery_status,
      },
    });
  },

  // POST /conversations/{reference}/typing
  async typing(reference) {
    const conversation = findByReference(reference);
    if (!conversation) {
      return notFound();
    }
    await wait(LATENCY_MS);
    return { status: 204, data: null };
  },

  // POST /conversations/{reference}/read
  async markRead(reference, lastMessageId) {
    const conversation = findByReference(reference);
    if (!conversation) {
      return notFound();
    }
    conversation.last_read_message_id = Math.max(
      conversation.last_read_message_id,
      parseInt(lastMessageId, 10) || 0
    );
    return ok({ unread_count: unreadCount(conversation) });
  },

  // POST /conversations/{reference}/csat
  async submitCsat(reference, payload) {
    const conversation = findByReference(reference);
    if (!conversation) {
      return notFound();
    }
    const score = parseInt((payload || {}).score, 10);
    if (!score || score < 1 || score > 5) {
      return fail(422, { validationErrors: { score: "The score must be between 1 and 5." } });
    }
    conversation.csat_submitted = true;
    return ok({ message: "Thank you for your feedback" });
  },

  // GET /conversations/{uuid}/sync
  async sync(uuid, params) {
    const conversation = state.conversations.find((c) => c.uuid === uuid) || null;
    if (!conversation) {
      return notFound();
    }
    const query = params || {};
    const sinceId = parseInt(query.since_id, 10) || 0;
    let limit = parseInt(query.limit, 10);
    limit = Number.isNaN(limit) ? SYNC_DEFAULT_LIMIT : Math.max(1, Math.min(limit, SYNC_MAX_LIMIT));

    const rows = conversation.messages
      .filter((m) => m.id > sinceId && m.visibility === "public" && m.delivery_status !== "suppressed")
      .slice(0, limit);

    return ok({
      conversation: {
        uuid: conversation.uuid,
        status: conversation.status,
        assignee_display: conversation.assignee_name,
        updated_at: conversation.updated_at,
      },
      messages: rows.map((m) => ({
        id: m.id,
        author_type: m.author_type,
        author_display: m.author_name,
        body: m.body,
        visibility: m.visibility,
        delivery_status: m.delivery_status,
        created_at: m.created_at,
      })),
      watermark: rows.length ? rows[rows.length - 1].id : sinceId,
      server_ts: Math.floor(new Date().getTime() / 1000),
    });
  },
};

export default supportMocks;
